import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts'; // still usable as a wrapper

import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import type { RootState } from '../../../redux/store';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import ToggleWithIcons from '../../../shared-components/toggle-with-icons/ToggleWithIcons';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import stationManagement from '../../../utils/api/station-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string; // month/day/hour
  totalRevenue: number;
  totalAmountPaid: number;
  totalPayableAmount: number;
  monthName: string;
}

/**
 * Energy Revenue Overlay Chart
 * @returns {any} The rendered component
 */
function EnergyRevenueOverlayChart() {
  const { t } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const { selectedStationIds } = useAppSelector((state) => state.stationComparison);
  const chargingStationList = useAppSelector(
    (state: RootState) => state.stationManagement.chargingStationList,
  );
  const [activeToggleKey, setActiveToggleKey] = useState('powerConsumption');
  const chartToggle = useMemo(
    () => [
      {
        label: t('energy'),
        key: 'powerConsumption',
        isActive: activeToggleKey === 'powerConsumption',
      },
      {
        label: t('revenueOverlay'),
        key: 'revenue',
        isActive: activeToggleKey === 'revenue',
      },
    ],
    [t, activeToggleKey],
  );
  const [activeIndexToggle, setActiveIndexToggle] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  //    const [graphData, setGraphData] = useState<(Partial<ChartDataProperties> & { label: string })[]>(
  //     [],
  //   );
  const [chartData, setChartData] = useState<any>(null);
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          /**
           * hides all legend items (no “radio buttons”)
           * @returns \
           */
          filter: () => false,
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false }, //  remove Y grid lines
        border: { display: false }, //  remove Y axis line
        ticks: {
          display: true, // SHOW ticks
          count: 5,
          /**
           * Showing value with kW
           * @param {any} value The tick value
           * @returns {string} The formatted tick
           */
          callback: (value) => `${value} ${t('kW')}`, // FORMAT ticks
          color: '#6b7280', // optional: nice gray
          font: {
            size: 11,
            weight: 500,
          },
        },
      },
    },
  };
  const stationIdsToUse = useMemo(() => {
    const selected = selectedStationIds.filter(Boolean);

    // If user selected stations → use them
    if (selected.length > 0) {
      return selected;
    }

    // Otherwise → use all charging stations
    return chargingStationList.map((station) => station._id);
  }, [selectedStationIds, chargingStationList]);

  /**
   * Fetches the revenue vs payable report data for the specified time range and updates the chart.
   * @param range - The time period for the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
    type: string,
  ) => {
    if (chargingStationList.length === 0) return;

    setLoading(true);
    const chargingStationsToSend = stationIdsToUse.length > 1 ? 'all' : stationIdsToUse;

    const payload =
      chargingStationsToSend === 'all'
        ? {
            allStationList: true,
            timePeriod: range,
            type,
          }
        : {
            chargingStations: chargingStationsToSend,
            timePeriod: range,
            type,
          };

    try {
      const response = await stationManagement.fetchEnergyRevenueComparison(payload);
      if (response.status === 200) {
        const { data } = response;

        let filledData: any[] = [];
        switch (range) {
          case 'last12months':
            filledData = fillMissingMonthsGeneric(data);
            break;
          case 'lastMonth':
            filledData = fillMissingLastNDaysGeneric(data, 30);
            break;
          case 'last7days':
            filledData = fillMissingLastNDaysGeneric(data, 7);
            break;
          case 'last24hours':
            filledData = fillMissingLast24HrsGeneric(data);
            break;
          default:
            filledData = data;
        }

        setGraphData(filledData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Function to switch the tab
   * @param {string} key The tab key to switch to
   */
  const handleClickToggle = (key: string) => {
    setActiveToggleKey(key);

    fetchReport(chartTimePeriod[activeIndexToggle]?.value || 'last24hours', key || 'userCount');
  };

  useEffect(() => {
    if (chargingStationList.length === 0) return;

    fetchReport(chartData || 'last24hours', activeToggleKey || 'userCount');
  }, [stationIdsToUse]);

  const labels = graphData.map((d) => d.label || d.monthName);

  // Extract all unique station IDs dynamically
  const allStationIds = Array.from(
    new Set(graphData.flatMap((d: any) => Object.keys(d.stationData || {}))),
  );

  const colorPalette = ['#00b894', '#6c5ce7', '#d63031', '#0984e3'];

  const datasets = allStationIds.map((stationId, index) => ({
    label: `${t('station')} ${stationId.slice(0, 6)}`,
    data: graphData.map((d: any) => d.stationData?.[stationId] ?? 0),
    borderColor: colorPalette[index % colorPalette.length],
    backgroundColor: `${colorPalette[index % colorPalette.length]}33`,
    fill: false,
    tension: 0.4,
    borderWidth: 1.5,
    pointRadius: 0,
    pointHoverRadius: 3,
  }));

  return (
    <div className="energy-revenue-chart-wrap">
      <div className="energy-revenue-chart-container">
        <div className="energy-revenue-header">
          <div className="energy-revenue-title">
            <ToggleWithIcons
              leftMenu={chartToggle[0]}
              rightMenu={chartToggle[1]}
              handleClickToggle={handleClickToggle}
            />
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => t(duration.value))}
            onToggle={(index) => {
              const selectedValue = chartTimePeriod[index].value;
              fetchReport(selectedValue || 'last24hours', activeToggleKey || 'userCount');
              setActiveIndexToggle(index);
              setChartData(selectedValue);
            }}
            defaultActiveIndex={activeIndexToggle}
            type="chart"
          />
        </div>
        <div className="energy-revenue-chart-container">
          <div className="energy-revenue-chart">
            {loading ? (
              <div className="loader">
                <BatteryLoader />
              </div>
            ) : (
              <ResponsiveContainer height={230}>
                <Line
                  data={{
                    labels,
                    datasets,
                  }}
                  options={chartOptions}
                />
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EnergyRevenueOverlayChart;
